import type { ReferralInfo } from '@getstep/sdk/dist/http/models/Referrals'
import { when } from 'mobx'
import { observer } from 'mobx-react-lite'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { useEffect } from 'react'

import type { VideoType } from '@app-types/video'
import { PublicProfile } from '@components/public-profile'
import { getOrCreateContentfulClient } from '@graphql/client'
import type { PageFragment } from '@graphql/fragments/PageFragment.graphql'
import {
    getCustomReferralInfo,
    getCustomReferralPages,
} from '@graphql/hooks/custom-referral-info'
import { AssetTag, getRandomVideosByTags } from '@graphql/hooks/video'
import { FeatureFromURL, useFeatureFromURL } from '@lib/hooks/useFeatureFromURL'
import { PersonalizationProvider } from '@lib/hooks/usePersonalizations'
import { fetchContentfulPage } from '@lib/static/fetchStaticPage'
import { DEFAULT_PAGE_TTL } from '@lib/static/static-page'
import { SdkClientStore } from '@lib/store/SdkClientStore'
import { useStores } from '@lib/store/useStores'
import type { PageWithContext } from '@lib/utils/context'
import { formatCurrency, pointsToDollars } from '@lib/utils/currency'
import { compact } from '@lib/utils/omit'
import { PAGE_OPTIONS } from '@lib/utils/page'
import { loadReferralInfo, mergeInfoWithCMS } from '@lib/utils/referral'
import { TEXT } from '@lib/utils/text'
import { PageTemplate } from '@page-templates/generic-page'
import { LINK } from '@shared/util/link'

const toCustomSlug = (code = '') =>
    PAGE_OPTIONS.REFERRAL.slug.replace('[code]', code)

const Referral: PageWithContext<{
    info?: ReferralInfo
    code: string
    slug: string
    hasPreferredVersion: boolean
    page: PageFragment
    videos: VideoType[]
}> = observer(({ info, code, slug, hasPreferredVersion, page, videos }) => {
    const expedited = useFeatureFromURL(FeatureFromURL.EXPEDITED_ONBOARDING)
    const { linkingStore } = useStores()

    useEffect(
        () =>
            when(
                () => linkingStore.loaded,
                () =>
                    linkingStore.updateReferralInfo(code, info, {
                        expedited_onboarding: expedited,
                    }),
            ),
        [code, info],
    )

    if (!hasPreferredVersion && !info) {
        // getServerSideProps will throw a 404
        return null
    }

    const { fullName, pictureUrl, firstName, referredAward } = info ?? {}

    const dollarValue = referredAward ? pointsToDollars(referredAward) : null
    const valueSuffix = dollarValue ? ` (${dollarValue} value)` : ''

    return (
        <PersonalizationProvider
            value={{
                pictureUrl,
                name: firstName,
                fullName,
                firstName,
                referredAwardFormatted: referredAward
                    ? formatCurrency(referredAward, {
                          optionalFraction: true,
                          longCurrencyName: true,
                      }) + valueSuffix
                    : '',
                referredAwardFormattedShort: referredAward
                    ? formatCurrency(referredAward, {
                          optionalFraction: true,
                      })
                    : '',
            }}
        >
            {!hasPreferredVersion && (
                <PublicProfile
                    hasScrollButton
                    hasAward
                    videos={videos}
                    displayName={fullName}
                    pictureUrl={pictureUrl}
                    subtitle={TEXT.invitedYouToJoinStep}
                    primary={{
                        title: TEXT.joinStep,
                        type: 'ActionDownloadApp',
                    }}
                    bottom={TEXT.learnMoreAboutStep}
                />
            )}

            <PageTemplate
                slug={slug}
                pageData={page}
                meta={{
                    ...PAGE_OPTIONS.REFERRAL.meta,
                    canonical: LINK.referral(code),
                    params: { 'Referral Code': code },
                    ogImage: hasPreferredVersion
                        ? undefined
                        : LINK.referralImage(code),
                }}
            />
        </PersonalizationProvider>
    )
})

export const getStaticPaths: GetStaticPaths = async () => {
    const client = getOrCreateContentfulClient(`/r/[code]`)

    const api = await SdkClientStore.create().createPublicApi()
    const referrals = await getCustomReferralPages(client)

    const paths = []

    for (const referral of referrals?.data?.pageCollection?.items ?? []) {
        let code = referral.slug
        if (code === undefined) continue
        code = code.substring(code.lastIndexOf('/') + 1)
        try {
            if (await api.getReferralInfo(code)) {
                paths.push({ params: { code } })
            }
        } catch (e) {
            console.warn(`Ignoring invalid referral code: ${code}`, e)
        }
    }

    return {
        paths,
        fallback: 'blocking', //  generate remaining pages on demand
    }
}

export const getStaticProps: GetStaticProps = async (context) => {
    const code = context?.params?.code

    if (typeof code !== 'string') {
        return { notFound: true }
    }

    if (code !== code.toUpperCase()) {
        return {
            props: {},
            redirect: {
                destination: LINK.referral(code).serialize(),
            },
        }
    }

    const customSlug = toCustomSlug(code)

    const client = getOrCreateContentfulClient(`/r/${code}`)

    const [info, referralInfo] = await Promise.all([
        loadReferralInfo(code),
        getCustomReferralInfo(client, {
            variables: {
                referralCode: code,
                slug: customSlug,
            },
        }),
    ])

    const {
        data: {
            pages: { total: preferredPageCount } = { total: 0 },
            info: infoFromCMS,
        },
    } = referralInfo

    // preferred version is one that doesnt show a profile card,
    // but instead shows sections, e.g. http://local.step.com:3000/r/BUGHA
    const hasPreferredVersion = preferredPageCount > 0

    /**
     * If the page has preferred version, we'll fetch it from the CMS and render it instead of the regular one.
     * The contents can be found in Contentful with slug that includes referral code.
     */
    const randomVideos = getRandomVideosByTags(client, [AssetTag.PayMePage])

    const videos = hasPreferredVersion ? [] : await randomVideos

    const cmsData = infoFromCMS?.items[0]

    if (!preferredPageCount && !info) {
        return { notFound: true }
    }

    const pageSlug = hasPreferredVersion
        ? customSlug
        : PAGE_OPTIONS.REFERRAL.slug

    const page = await fetchContentfulPage(pageSlug)

    return {
        props: {
            ...compact({
                info: mergeInfoWithCMS(info, cmsData),
                code,
                slug: pageSlug,

                hasPreferredVersion,
            }),
            page,
            videos,
        },
        revalidate: DEFAULT_PAGE_TTL,
    }
}

export default Referral
